import { css } from '@emotion/react';
import styled from '@emotion/styled';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import type { Theme as MuiTheme } from '@mui/material/styles';
import { Text } from 'components/text/Text';
import { colors } from 'styles/theme/colors';
import { palette } from 'styles/theme/palette';
import { DOCUMENT_PREVIEW_HEIGHT_PX } from 'features/files/components/filePreview.styles';

export {
  DOCUMENT_PREVIEW_HEIGHT_PX,
  PdfCanvasClip,
  PreviewCanvas,
  ImgPreviewClip,
  PreviewImg,
  NonPdfFallback,
  PreviewToolbarFloating,
  PageNumberField,
  PageNavFraction,
  OfWord,
  TotalPagesLabel,
} from 'features/files/components/filePreview.styles';

/** Root `id` on {@link FileUploadFieldsTable}. */
export const FILE_UPLOAD_FIELDS_TABLE_NAME = 'file-upload-form-fields';

/** Body cells for {@link FileUploadFieldsTable}; `$colWidth` maps to column `size` / {@link FILE_UPLOAD_TABLE_COL_WIDTH}. */
export const FileUploadFieldsTableBodyCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== '$colWidth',
})<{ $colWidth: number }>`
  width: ${p => p.$colWidth}px;
  max-width: ${p => p.$colWidth}px;
  min-width: 0;
  vertical-align: middle;
  box-sizing: border-box;
  padding: 10px 24px;
  border-bottom: 1px solid ${colors.grey[200]};
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: normal;
  text-align: left;

  & > * {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }
`;

/** MUI `TableContainer` for file-upload form fields; nested rules use `&` (see `tableStyles.ts` / `TableContainerStyled`). */
export const FileUploadFieldsTableSurface = styled(TableContainer)`
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  & .MuiTable-root {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    table-layout: fixed;
    border-collapse: collapse;
  }

  & .MuiTableHead-root .MuiTableCell-root {
    min-width: 0;
    height: 44px;
    min-height: 44px;
    max-height: 44px;
    box-sizing: border-box;
    padding: 0 24px;
    vertical-align: middle;
    text-align: left;
    background-color: ${p => p.theme.palette.bg.brandSubtle};
    color: ${p => p.theme.palette.text.secondary};
    font-weight: 600;
    line-height: 1.25;
    border-bottom: 0.5px solid ${p => p.theme.palette.border.brand};
  }

  /* First column: label typography (Text → MuiTypography). */
  & tbody tr td:first-of-type .MuiTypography-root {
    font-size: 0.75rem;
    line-height: 1.25;
  }

  & tbody tr td:has(.MuiOutlinedInput-root) .MuiInputLabel-root,
  & tbody tr td:has(.MuiOutlinedInput-root) label.MuiFormLabel-root,
  & tbody tr td:has(button.MuiButton-root) .MuiInputLabel-root,
  & tbody tr td:has(button.MuiButton-root) label.MuiFormLabel-root {
    display: none;
  }

  & tbody tr td:has(.MuiOutlinedInput-root) .MuiFormControl-root,
  & tbody tr td:has(button.MuiButton-root) .MuiFormControl-root {
    margin: 0;
    width: 100%;
  }

  & tbody tr td:has(.MuiOutlinedInput-root) .MuiOutlinedInput-root {
    width: 100%;
    margin-top: 0;
    margin-bottom: 0;
    box-sizing: border-box;
  }

  & tbody tr td:has([data-file-upload-control]) .MuiAutocomplete-root {
    margin: 0;
  }

  &
    tbody
    tr
    td:has([data-file-upload-control])
    .MuiOutlinedInput-root:not(.Mui-error):not(.Mui-disabled):not(:hover):not(.Mui-focused)
    .MuiOutlinedInput-notchedOutline {
    border-color: ${p => p.theme.palette.border.primarySubtle};
  }

  /* CustomDropDown trigger: scope by MUI Button + optional data-has-value on the trigger (component API). */
  & tbody tr td:has(button.MuiButton-root) button.MuiButton-root {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    border-radius: 8px;
  }

  &
    tbody
    tr
    td:has(button.MuiButton-root)
    button.MuiButton-root:not(:disabled):not(.Mui-disabled)[data-has-value]
    [data-custom-dropdown-trigger-content] {
    color: ${palette.text.primary};
  }

  &
    tbody
    tr
    td:has(button.MuiButton-root)
    button.MuiButton-root:not(:disabled):not(.Mui-disabled):not([data-has-value])
    [data-custom-dropdown-trigger-content] {
    color: ${palette.text.placeholder};
  }

  & tbody tr td:has(button.MuiButton-root) button.MuiButton-root:disabled,
  & tbody tr td:has(button.MuiButton-root) button.MuiButton-root.Mui-disabled {
    color: ${palette.text.disabled};
    background-color: ${palette.bg.disabledSubtle};
    border: 0.5px solid ${palette.border.primary};
    opacity: 1;
  }

  &
    tbody
    tr
    td:has(button.MuiButton-root)
    button.MuiButton-root:disabled
    [data-custom-dropdown-trigger-content],
  &
    tbody
    tr
    td:has(button.MuiButton-root)
    button.MuiButton-root.Mui-disabled
    [data-custom-dropdown-trigger-content] {
    color: ${palette.text.disabled};
  }

  & tbody tr td:has(button.MuiButton-root) button.MuiButton-root:disabled .MuiButton-endIcon,
  & tbody tr td:has(button.MuiButton-root) button.MuiButton-root.Mui-disabled .MuiButton-endIcon {
    color: ${palette.text.disabled};
  }

  &
    tbody
    tr
    td:has(button.MuiButton-root)
    button.MuiButton-root:not(:disabled):not(.Mui-disabled)[data-has-value]
    .MuiButton-endIcon {
    color: ${palette.text.primary};
  }

  &
    tbody
    tr
    td:has(button.MuiButton-root)
    button.MuiButton-root:not(:disabled):not(.Mui-disabled):not([data-has-value])
    .MuiButton-endIcon {
    color: ${palette.text.placeholder};
  }
`;

/** Label / static text in the first table column (see {@link FileUploadFieldsTableSurface}). */
export const FileUploadFieldLabelText = styled(Text)({
  display: 'block',
  width: '100%',
  textAlign: 'left',
  color: colors.grey[800],
  fontWeight: 500,
});

export const FileUploadFieldOptionalHint = styled(Text)({
  color: colors.grey[500],
  fontWeight: 400,
});

export const RequiredStar = styled('span')({
  color: colors.error[500],
  marginLeft: 2,
});

/** MUI `Theme` from ThemeProvider (`spacing` as a function, `shape`, `typography`, …). */
function isMuiTheme(theme: object): theme is MuiTheme {
  return typeof Reflect.get(theme, 'spacing') === 'function';
}

function mui(theme: object): MuiTheme {
  if (!isMuiTheme(theme)) {
    throw new Error('Expected MUI Theme from ThemeProvider');
  }
  return theme;
}

/** Design token: upload / file-preview panel fill (Figma Background secondary). */
export const uploadSectionBackground = 'var(--Background-Secondary-bg-secondary, #FAF7F5)';

const hideScrollbars = {
  scrollbarWidth: 'none' as const,
  msOverflowStyle: 'none' as const,
  '&::-webkit-scrollbar': {
    display: 'none',
    width: 0,
    height: 0,
  },
};

/** Two-column grid root for {@link FileUploadForm}. */
export const FormLayoutRoot = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 35%) minmax(0, 65%)',
  gap: '24px',
  alignItems: 'start',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  overflowX: 'hidden',
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
});

export const LeftColumn = styled('div')(({ theme }) => ({
  minWidth: 0,
  minHeight: 0,
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing.xs,
  alignSelf: 'start',
  boxSizing: 'border-box',
}));

/** Right column layout when embedding {@link FileUploadFieldsTableSurface}. */
export const RightColumn = styled('div', {
  shouldForwardProp: p => p !== '$embedTable',
})<{ $embedTable?: boolean }>`
  ${({ $embedTable }) =>
    $embedTable &&
    css`
      align-self: start;
      min-width: 0;
      min-height: 0;
      border: none;
      border-radius: 0;
      overflow-x: hidden;
      overflow-y: visible;
      background-color: ${colors.white};
      display: flex;
      flex-direction: column;
      flex: 0 1 auto;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    `}
`;

/** Column wrapper root for {@link FileUploadSection}. */
export const UploadSectionRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: mui(theme).spacing(0),
  height: DOCUMENT_PREVIEW_HEIGHT_PX,
  minHeight: DOCUMENT_PREVIEW_HEIGHT_PX,
  maxHeight: DOCUMENT_PREVIEW_HEIGHT_PX,
  flex: '0 0 auto',
  minWidth: 0,
  maxWidth: '100%',
  alignSelf: 'stretch',
  boxSizing: 'border-box',
  overflow: 'hidden',
}));

export const UploadSectionFileName = styled(Text)(({ theme }) => ({
  flexShrink: 0,
  display: 'block',
  width: '100%',
  /* Array theme.spacing: use integer indices only ([0,2,4,8,12,16,...] → top/sides/bottom ≈ 12/16/8px). */
  padding: mui(theme).spacing(4, 5, 3),
  backgroundColor: uploadSectionBackground,
}));

/** File name (left) + optional remove/replace (right). Title/actions via `data-slot`. */
export const UploadPreviewHeaderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: mui(theme).spacing(2),
  padding: mui(theme).spacing(4, 5, 3),
  backgroundColor: uploadSectionBackground,
  minWidth: 0,
  '& [data-slot="title"]': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& [data-slot="actions"]': {
    display: 'flex',
    alignItems: 'center',
    gap: mui(theme).spacing(2),
    flexShrink: 0,
  },
}));

export const PreviewShell = styled('div')(({ theme }) => ({
  flex: '1 1 0%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  height: '100%',
  borderRadius: 8,
  border: `1px solid ${colors.grey[200]}`,
  backgroundColor: uploadSectionBackground,
  overflow: 'hidden',
  minWidth: 0,
  maxWidth: '100%',
  padding: 0,
}));

export const PreviewViewport = styled('div')(({ theme }) => ({
  flex: '1 1 0%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  maxWidth: '100%',
  backgroundColor: uploadSectionBackground,
  overflow: 'hidden',
  paddingLeft: mui(theme).spacing(2),
  paddingRight: mui(theme).spacing(2),
  paddingBottom: mui(theme).spacing(2),
}));

/** Centered spinner + label while upload is pending (inside {@link PreviewViewport}). */
export const UploadPendingState = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 0,
  gap: mui(theme).spacing(1),
}));

export const DocumentStage = styled('div')(() => ({
  flex: '1 1 0%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  minHeight: 0,
  overflow: 'hidden',
}));

export const DocumentSheet = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  flex: '1 1 0%',
  minHeight: 0,
  height: '100%',
  maxHeight: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  backgroundColor: colors.white,
  borderRadius: mui(theme).shape.borderRadius,
  boxShadow: '0 4px 24px rgba(25, 25, 27, 0.08), 0 1px 3px rgba(25, 25, 27, 0.06)',
  overflow: 'hidden',
  ...hideScrollbars,
}));

/** Row of remove/replace actions above the preview. */
export const UploadActionsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexShrink: 0,
  gap: mui(theme).spacing(2),
  paddingLeft: mui(theme).spacing(2),
  paddingRight: mui(theme).spacing(2),
  paddingBottom: mui(theme).spacing(1),
  paddingTop: 0,
}));

export const DropZone = styled('div', {
  shouldForwardProp: p => p !== '$active',
})<{ $active: boolean }>(({ theme, $active }) => ({
  flex: '1 1 0%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 0,
  padding: mui(theme).spacing(4, 3),
  borderRadius: 8,
  borderWidth: 1,
  borderStyle: 'dashed',
  borderColor: $active ? colors.brand[400] : colors.grey[300],
  backgroundColor: uploadSectionBackground,
  boxShadow: $active ? `inset 0 0 0 1px ${colors.brand[200]}` : 'none',
  cursor: 'default',
  maxWidth: '100%',
  boxSizing: 'border-box',
}));

export const BrowseLink = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  font: 'inherit',
  color: colors.marineBlue[500],
  cursor: 'pointer',
  textDecoration: 'underline',
  '&:hover': {
    color: colors.marineBlue[600],
  },
});
